import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Grid,
  Chip,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleIcon from "@mui/icons-material/People";
import EventIcon from "@mui/icons-material/Event";
import placeHolderImage from "assets/images/no-image-placeholder.jpg";
import ArgonButton from "components/ArgonButton";
import { getReportById } from "services/reportService";
import { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { REPORT_ACTIONS } from "./consts";
import { resolveReport } from "services/reportService";
import "./ReviewPanelModal.css";
import { toast } from "react-toastify";
import AvatarWithLoader from "../Avatar/AvatarWithLoader";
import { formatReasonOrContentType } from "services/utilities";

function ActivityReviewPanelModal({ open, handleClose, _id, onUpdate }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    try {
      setLoading(true);
      if (_id) {
        getReportById(_id).then((res) => {
          if (res.success && res.data) {
            setReport(res.data);
          } else {
            setReport(null);
          }
          setLoading(false);
        });
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      setReport(null);
      setLoading(false);
    }
  }, [_id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleAction = (action) => {
    resolveReport(_id, action).then((res) => {
      if (res.success) {
        toast.info("Action taken successfully");
        handleClose();
        onUpdate();
      } else {
        toast.error("Failed to take action");
      }
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Box display="flex" justifyContent="center" alignItems="center" pt={3}>
        <Typography fontSize={20} fontWeight={600} color={"#344767"}>
          Activity Review Panel
        </Typography>
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <DialogContent sx={{ p: 4 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
            sx={{ flexWrap: "wrap", gap: 2 }}
          >
            <MetaItem
              label="Reported User"
              name={report?.reportedAgainst?.name}
              img={report?.reportedAgainst?.avatar}
            />
            <MetaItem
              label="Reporter"
              name={report?.reporter?.name}
              img={report?.reporter?.avatar}
            />
            <MetaItem label="Content Type" value="Activity" />
            <MetaItem
              label="Report Reason"
              value={formatReasonOrContentType(report?.reason)}
            />
          </Box>

          <Grid
            container
            borderColor={"#E0E0E0"}
            display="flex"
            justifyContent="center"
            sx={{
              backgroundColor: "#fff",
              borderRadius: 3,
              boxShadow: 2,
              p: 2,
            }}
          >
            <Grid
              item
              xs={12}
              md={7}
              sx={{
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <img
                  alt={report?.content?.title || "Activity"}
                  src={report?.content?.image || placeHolderImage}
                  style={{
                    width: 350,
                    height: 200,
                    objectFit: "cover",
                    borderRadius: 10,
                    marginTop: 20,
                  }}
                />
                <Box>
                  <Typography
                    fontSize={20}
                    fontWeight={600}
                    color={"#172B4D"}
                    mt={1}
                  >
                    {report?.content?.title || "Untitled Activity"}
                  </Typography>
                  {report?.content?.category_id && (
                    <Chip
                      label={report?.content?.category_id?.name}
                      size="small"
                      sx={{
                        backgroundColor: "#fff7e6",
                        color: "#d46b08",
                        fontWeight: 600,
                      }}
                    />
                  )}
                  <Typography
                    fontSize={16}
                    fontWeight={400}
                    color={"#4F4F4F"}
                    mt={1}
                    mb={1}
                  >
                    {report?.content?.description}
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <EventIcon fontSize="small" />
                      <Typography fontSize={14} fontWeight={400}>
                        {formatDate(report?.content?.datetime)}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <PeopleIcon fontSize="small" />
                      <Typography fontSize={14} fontWeight={400}>
                        {report?.content?.participants?.length || 0}/
                        {report?.content?.max_participants || 0}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationOnIcon fontSize="small" />
                    <Typography fontSize={14} fontWeight={400}>
                      {report?.content?.address
                        ? report?.content?.address
                        : "Location not specified"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box p={2}>
                <Typography
                  fontSize={16}
                  fontWeight={600}
                  pb={1}
                  color={"#000000"}
                >
                  Participants ({report?.content?.participants?.length || 0})
                </Typography>
                {report?.content?.participants &&
                report?.content?.participants?.length > 0 ? (
                  report?.content?.participants?.map((user, idx) => (
                    <Box
                      key={user._id || idx}
                      display="flex"
                      alignItems="center"
                      mb={1}
                      p={1}
                      boxShadow={"0 0 5px rgba(0, 0, 0, 0.2)"}
                      sx={{
                        borderRadius: 4,
                      }}
                    >
                      <AvatarWithLoader src={user.avatar} alt={user.name} />
                      <Typography variant="body4" color={"#4F4F4F"}>
                        {user.name || "Unknown User"}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No participants yet
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="center" mt={4} gap={1}>
            {report?.isResolved ? (
              // Show only cancel/close button when resolved
              <ArgonButton
                className="review-panel-action-button"
                onClick={handleClose}
              >
                Close
              </ArgonButton>
            ) : (
              // Show all action buttons when not resolved
              <>
                <ArgonButton
                  className="review-panel-action-button"
                  onClick={handleClose}
                >
                  Cancel
                </ArgonButton>
                <ArgonButton
                  className="review-panel-action-button"
                  onClick={() => handleAction(REPORT_ACTIONS.WARN)}
                >
                  ⚠️ Warn
                </ArgonButton>
                {/* <ArgonButton
                  className="review-panel-action-button"
                  onClick={() => handleAction(REPORT_ACTIONS.MUTE_USER)}
                >
                  🤐 Mute User
                </ArgonButton> */}
                <ArgonButton
                  className="review-panel-action-button"
                  onClick={() => handleAction(REPORT_ACTIONS.DELETE_CONTENT)}
                >
                  ❌ Delete Activity
                </ArgonButton>
                <ArgonButton
                  className="review-panel-action-button"
                  onClick={() => handleAction(REPORT_ACTIONS.BAN_USER)}
                >
                  ⛔ Ban User
                </ArgonButton>
                <ArgonButton
                  className="review-panel-action-button"
                  onClick={() => handleAction(REPORT_ACTIONS.DISMISS)}
                >
                  ✅ Dismiss Complain
                </ArgonButton>
              </>
            )}
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
}

function MetaItem({ label, name, value, img }) {
  return (
    <Box display="flex" flexDirection="column" alignItems="left" gap={1}>
      <Typography variant="h6" fontWeight={700} color="#000000">
        {label}
      </Typography>
      <Box display="flex" flexDirection="row" alignItems="center">
        {img && <AvatarWithLoader src={img} alt={name} />}
        <Box pb={1}>
          <Typography variant="h6" fontWeight={700} color="#7B809A">
            {name || value}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
export default ActivityReviewPanelModal;
